import { DocumentAPI } from './API'
import {
  DocumentTagsQuery,
  CreateTagPayload,
  UpdateTagPayload,
  DocumentTagDTO,
} from './dtos/document-tag'
import DocumentTag from './DocumentTag'
import {
  TagCreatedMessage,
  TagUpdatedMessage,
  TagDeletedMessage,
} from './dtos/realtime-messages'
import { PrimeProps, Id, CursorPaged } from 'types/definitions'
import { collection } from 'infra/collection'
import { GetCurrentMember } from 'iam/SessionStore'

export type PrimeDocumentTag = (
  props: PrimeProps<DocumentTagDTO, 'id'>,
) => DocumentTag | undefined

export type GetDocumentTag = (id: string | undefined) => DocumentTag | undefined

export default class DocumentTagStore {
  tags = collection<DocumentTagDTO, DocumentTag>({
    create: () => new DocumentTag(),
  })

  constructor(
    private api: DocumentAPI,
    private getCurrentMember: GetCurrentMember,
  ) {
    this.api.tags
      .when<TagCreatedMessage>('TagCreated')
      .subscribe((e) => this.tags.set(e.data))
    this.api.tags
      .when<TagUpdatedMessage>('TagUpdated')
      .subscribe((e) => this.tags.set(e.data))
    this.api.tags.when<TagDeletedMessage>('TagDeleted').subscribe((e) => {
      this.tags.remove(e.data.id)
    })
  }

  forWorkspace(workspaceId: string) {
    return this.tags.filter((x) => x.workspaceId === workspaceId)
  }

  async fetchTags(
    query?: DocumentTagsQuery,
  ): Promise<CursorPaged<DocumentTag>> {
    const params = {
      limit: 1000,
      workspace_id: Id.assert(this.getCurrentMember().workspace.id),
      ...query,
    }
    const paged = await this.api.tags.find(params)
    return CursorPaged.map(paged, (data) =>
      this.tags.set(
        // Can remove filter once the API filters on the backend
        data.filter((x) => x.workspace_id === params.workspace_id),
      ),
    )
  }

  async create(data: CreateTagPayload) {
    return this.api.tags.create(data).then((data) => this.tags.set(data))
  }

  async update(id: string, data: UpdateTagPayload) {
    return this.api.tags.update(id, data).then((data) => this.tags.set(data))
  }

  async delete(tagId: string) {
    return this.api.tags.delete(tagId).then(() => this.tags.remove(tagId))
  }
}
